import { s3URLToDNSURL } from "@/utils/helpers/urlHelpers";
import { apiClient } from "./client";

export interface Listing {
  _id: string;
  condition_details?: string | null;
  condition_name: string;
  current_price: number;
  datetime_last_updated: string;
  datetime_scraped: string;
  datetime_sold?: string | null;
  description: string;
  listing_url: string;
  affiliate_listing_url?: string | null;
  market_status: {
    avg_price: number;
    sold_avg_price: number;
    price_ratio: number;
  };
  price_events: Array<{
    datetime: string;
    event_type: string;
    price: number;
  }>;
  price_history: number[];
  product: {
    brand: string;
    category: string;
    model: string;
  };
  product_variant: {
    _id: string;
    colors: string[];
    dominant_color_family: string;
    dominant_material: string;
    hardwares: string[];
    size_name: string;
  };
  return_policy: string;
  s3_image_urls: string[];
  sku: string;
  social_proof?: unknown;
  sold_price?: number | null;
  ss_slug: string;
  standardized_condition: number;
  standardized_description: string[];
  status: string;
  vendor: string;
  vendor_condition: string;
  year_produced?: number | null;
  schema_org_json?: string;
  seo_metadata?: {
    seo_title: string;
    description: string;
    image_url?: string;
    canonical_url: string;
    open_graph: {
      title: string;
      description: string;
      image?: string;
      url: string;
      type: string;
      site_name: string;
    };
    twitter: {
      card: string;
      title: string;
      description: string;
      image?: string;
      site: string;
      creator: string;
    };
  };
}

export interface ListingComp extends Listing {
  is_good_deal?: boolean; // better condition or lower price
  isBestDeal?: boolean; // better condition and lower price
}
export interface PriceHistoryData {
  date: string;
  avgPrice: number;
  soldAvgPrice?: number;
  count: number;
}

export interface HistoricalPriceData {
  date: string;
  price: number;
}

export interface PriceHistoryResponse {
  price_history: PriceHistoryData[];
  historical_price_data: HistoricalPriceData[];
  similar_listings_count: number;
  sold_listings_count: number;
}

export interface CompsResponse {
  comps: ListingComp[];
}

export interface CompsCountsResponse {
  condition_counts: { value: string; label: string; count: number }[];
  dominant_color_counts: { value: string; label: string; count: number }[];
}

export interface ListingRedirectResponse {
  redirect: boolean;
  ss_slug: string;
}

export interface ListingsFilters {
  sortOrder: string;
  minPrice: string;
  maxPrice: string;
  brands: string[];
  deals: string[];
  hardwares: string[];
  conditions: string[];
  colors: string[];
  vendors: string[];
  searchQuery?: string;
}

export interface ListingsResponse {
  brands: string[];
  listings: Listing[];
  page: number;
  totalPages: number;
}

export interface ListingsRequest extends Partial<ListingsFilters> {
  page?: number;
  limit?: number;
  searchQuery?: string;
}

class ListingsService {
  async getListings(params: ListingsRequest = {}): Promise<ListingsResponse> {
    const { page = 1, limit = 50, ...filters } = params;
    const queryParams = { page, limit, ...filters };
    return await apiClient.get<ListingsResponse>("/listings", queryParams);
  }

  async getListing(id: string): Promise<Listing> {
    return await apiClient.get<Listing>(`/listings/${id}`);
  }

  async getListingBySlug(slug: string): Promise<Listing> {
    return await apiClient.get<Listing>(`/listings/slug/${slug}`);
  }

  async getListingWithRedirect(identifier: string): Promise<{ listing: Listing | null; redirect?: string }> {
    try {
      const response = await apiClient.get<Listing | ListingRedirectResponse>(`/listings/${identifier}`);
      
      // Check if this is a redirect response
      if ('redirect' in response && response.redirect) {
        return { listing: null, redirect: response.ss_slug };
      }

      const listing = response as Listing;
      listing.s3_image_urls = listing.s3_image_urls.map((url: string) => s3URLToDNSURL(url));
      
      return { listing };
    } catch (error) {
      throw error;
    }
  }

  async getPriceHistory(listingId: string): Promise<PriceHistoryResponse> {
    const params = { listing_id: listingId };
    return await apiClient.get<PriceHistoryResponse>(`/listings/${listingId}/price-history`, params);
  }

  async getComparableListings(
    listingId: string,
    filters?: {
      size?: string;
      conditions?: string[];
      colors?: string[];
    }
  ): Promise<CompsResponse> {
    const params: Record<string, unknown> = {};
    
    if (filters?.size) {
      params.size = filters.size;
    }
    
    if (filters?.conditions && filters.conditions.length > 0) {
      params["conditions"] = filters.conditions;
    }
    
    if (filters?.colors && filters.colors.length > 0) {
      params["colors"] = filters.colors;
    }

    const response = await apiClient.get<CompsResponse>(`/listings/${listingId}/comps_v2`, params);

    return {
      comps: response.comps.map((comp: ListingComp) => ({
        ...comp,
        s3_image_urls: comp.s3_image_urls.map((url: string) => s3URLToDNSURL(url))
      }))
    };
  }

  async getCompsCounts(listingId: string): Promise<CompsCountsResponse> {
    return await apiClient.get<CompsCountsResponse>(`/listings/${listingId}/comps_counts_v2`);
  }

  async getBrands(): Promise<string[]> {
    const response = await apiClient.get<{ brands: string[] }>("/listings/brands");
    return response.brands;
  }

  async getFilters(): Promise<{
    brands: string[];
    conditions: string[];
    colors: string[];
    hardwares: string[];
    vendors: string[];
  }> {
    return await apiClient.get("/listings/filters");
  }
}

export const listingsService = new ListingsService();
